// Intégration DiceBear : génère les avatars (portraits) des personnages, GRATUITEMENT,
// dans le navigateur, à partir de la graine (avatarSeed). Aucune image à dessiner = 0 token.
//
// Principe : DiceBear produit un SVG -> on en fait un data-URI -> Phaser le rasterise en texture
// via `scene.load.svg(key, dataUri, { width, height })` (à appeler dans preload()).
//
// Note : DiceBear fait des PORTRAITS (buste de face), pas des spritesheets de marche 4 directions.
//   - Idéal pour : visage dans les dialogues, trombinoscope, et "standee" statique sur la carte.
//   - Pour un PNJ qui MARCHE avec animation, il faudra un spritesheet dédié (Kenney/LimeZu).
import 'phaser'
import { createAvatar } from '@dicebear/core'
import {
    bigSmile,
    avataaars,
    miniavs,
    funEmoji,
    adventurer,
    bigEars,
    personas,
    openPeeps,
    notionists,
    lorelei,
} from '@dicebear/collection'
import type { CharacterData } from '../data/characters'
import { type Appearance, appearanceToAvataaarsParams } from '../net/protocol'

// Mapping "kebab-case" (valeur de avatarStyle dans le JSON) -> style DiceBear.
const STYLES: Record<string, any> = {
    'big-smile': bigSmile,
    'avataaars': avataaars,
    'miniavs': miniavs,
    'fun-emoji': funEmoji,
    'adventurer': adventurer,
    'big-ears': bigEars,
    'personas': personas,
    'open-peeps': openPeeps,
    'notionists': notionists,
    'lorelei': lorelei,
}
const DEFAULT_STYLE = bigSmile

/** Taille de rendu par défaut des portraits (px). */
export const PORTRAIT_SIZE = 96

/** Clé de texture Phaser pour le portrait d'un personnage. */
export function portraitKey(id: string): string {
    return `portrait-${id}`
}

/** SVG brut du portrait (utile pour un affichage HTML/DOM hors Phaser). */
export function buildAvatarSvg(character: CharacterData, size: number = PORTRAIT_SIZE): string {
    const style = STYLES[character.avatarStyle] ?? DEFAULT_STYLE
    return createAvatar(style, { seed: character.avatarSeed, size }).toString()
}

/** data-URI prêt à charger dans Phaser (load.svg) ou à mettre dans un <img src>. */
export function buildAvatarDataUri(character: CharacterData, size: number = PORTRAIT_SIZE): string {
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(buildAvatarSvg(character, size))
}

// ── Personalized avatar (admin-set appearance) — avataaars style ─────────────
/** SVG of a DiceBear avatar built from an admin-set Appearance (gender + hair/skin/shirt colors). */
export function buildAppearanceSvg(appearance: Appearance, seed: string, size: number = PORTRAIT_SIZE): string {
    return createAvatar(avataaars, { size, ...appearanceToAvataaarsParams(appearance, seed) } as any).toString()
}

// (The on-map body is a recoloured Kenney walking sprite — see avatars/recolor.ts.)

/** (Re)load `portrait-<id>` from an Appearance into the global texture manager (Kazi card). */
export function loadAppearancePortrait(scene: Phaser.Scene, id: string, appearance: Appearance, onReady?: () => void, size: number = PORTRAIT_SIZE): void {
    const key = portraitKey(id)
    if (scene.textures.exists(key)) scene.textures.remove(key) // appearance may have changed → refresh
    const svg = buildAppearanceSvg(appearance, id, size)
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
    scene.load.svg(key, url, { width: size, height: size })
    scene.load.once(`filecomplete-svg-${key}`, () => { URL.revokeObjectURL(url); onReady?.() })
    scene.load.once(`fileerror-svg-${key}`, () => URL.revokeObjectURL(url))
    scene.load.start() // mid-game load is fine; texture is global once complete
}

/**
 * Met en file les textures de portraits dans le loader d'une scène.
 * À APPELER DANS preload(). Les clés sont `portrait-<id>`.
 *
 * IMPORTANT : on passe par une URL **blob:** (et non une data-URI). Le loader de Phaser refuse
 * les data-URIs (« Local data URIs are not supported ») et reste bloqué → preload ne se termine
 * jamais et create() ne tourne pas. Les URLs blob passent par XHR normalement.
 */
export function preloadPortraits(
    scene: Phaser.Scene,
    characters: CharacterData[],
    size: number = PORTRAIT_SIZE
): void {
    characters.forEach(character => {
        const key = portraitKey(character.id)
        if (scene.textures.exists(key)) return // déjà chargé (ex. scène déjà visitée)

        const svg = buildAvatarSvg(character, size)
        const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }))
        scene.load.svg(key, url, { width: size, height: size })
        // libère l'URL blob une fois le fichier chargé (ou en erreur)
        scene.load.once(`filecomplete-svg-${key}`, () => URL.revokeObjectURL(url))
        scene.load.once(`fileerror-svg-${key}`, () => URL.revokeObjectURL(url))
    })
}
